import { httpsCallable, type HttpsCallable } from 'firebase/functions';
import { ref, uploadBytesResumable } from 'firebase/storage';
import { z } from 'zod';

import { functions, storage } from '../firebase.js';
import { SchemaValidationFailedError } from '@kilat/shared';

/**
 * Sprint 87 — typed wrappers for profile + KYC self-service.
 *
 * Three callables on the server (`updateAgentProfile`,
 * `updateRecruiterProfile`, `submitKycDocument`) plus one client-side
 * helper (`uploadKycDocument`) that runs the resumable Storage upload
 * before calling `submitKycDocument` to register the path.
 */

// ── updateAgentProfile / updateRecruiterProfile ────────────────────────

export const UpdateProfileInputSchema = z.object({
  name: z.string().min(1).max(120),
  phone: z.string().min(7).max(20),
  locale: z.enum(['en', 'ms', 'zh', 'ta']),
});
export type UpdateProfileInput = z.infer<typeof UpdateProfileInputSchema>;

const UpdateProfileOutputSchema = z.object({ ok: z.literal(true) });
type UpdateProfileOutput = z.infer<typeof UpdateProfileOutputSchema>;

const updateAgentProfileFn: HttpsCallable<UpdateProfileInput, UpdateProfileOutput> = httpsCallable(
  functions,
  'updateAgentProfile',
);
const updateRecruiterProfileFn: HttpsCallable<UpdateProfileInput, UpdateProfileOutput> =
  httpsCallable(functions, 'updateRecruiterProfile');

export async function callUpdateAgentProfile(input: UpdateProfileInput): Promise<void> {
  const parsed = UpdateProfileInputSchema.safeParse(input);
  if (!parsed.success) {
    throw new SchemaValidationFailedError('updateAgentProfile.input', parsed.error.flatten());
  }
  const result = await updateAgentProfileFn(parsed.data);
  const out = UpdateProfileOutputSchema.safeParse(result.data);
  if (!out.success) {
    throw new SchemaValidationFailedError('updateAgentProfile.output', out.error.flatten());
  }
}

export async function callUpdateRecruiterProfile(input: UpdateProfileInput): Promise<void> {
  const parsed = UpdateProfileInputSchema.safeParse(input);
  if (!parsed.success) {
    throw new SchemaValidationFailedError('updateRecruiterProfile.input', parsed.error.flatten());
  }
  const result = await updateRecruiterProfileFn(parsed.data);
  const out = UpdateProfileOutputSchema.safeParse(result.data);
  if (!out.success) {
    throw new SchemaValidationFailedError('updateRecruiterProfile.output', out.error.flatten());
  }
}

// ── submitKycDocument ──────────────────────────────────────────────────

export const SubmitKycDocumentInputSchema = z.object({
  persona: z.enum(['agent', 'recruiter']),
  storagePath: z.string().min(1).max(500),
  contentType: z.string().max(120).optional(),
  sizeBytes: z.number().int().nonnegative().optional(),
});
export type SubmitKycDocumentInput = z.infer<typeof SubmitKycDocumentInputSchema>;

const SubmitKycDocumentOutputSchema = z.object({ ok: z.literal(true) });

const submitKycFn: HttpsCallable<
  SubmitKycDocumentInput,
  z.infer<typeof SubmitKycDocumentOutputSchema>
> = httpsCallable(functions, 'submitKycDocument');

async function callSubmitKycDocument(input: SubmitKycDocumentInput): Promise<void> {
  const parsed = SubmitKycDocumentInputSchema.safeParse(input);
  if (!parsed.success) {
    throw new SchemaValidationFailedError('submitKycDocument.input', parsed.error.flatten());
  }
  const result = await submitKycFn(parsed.data);
  const out = SubmitKycDocumentOutputSchema.safeParse(result.data);
  if (!out.success) {
    throw new SchemaValidationFailedError('submitKycDocument.output', out.error.flatten());
  }
}

// ── KYC upload helper ──────────────────────────────────────────────────

const ALLOWED_MIME = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];
const MAX_BYTES = 8 * 1024 * 1024; // 8 MB — generous for IC scans

export interface KycUploadResult {
  readonly storagePath: string;
  readonly downloadURL?: string;
  readonly sizeBytes: number;
  readonly contentType: string;
}

/**
 * Resumable upload to the per-persona kyc folder, then register the
 * path with the server. The Storage rule already gates writes to the
 * owner; the server callable rechecks the path matches the claim's id.
 *
 * `onProgress` reports 0..1 percent for the upload UX. Throws on
 * MIME / size validation, Storage error, or callable failure — let
 * the caller render the message inline.
 */
export async function uploadKycDocument(args: {
  readonly persona: 'agent' | 'recruiter';
  readonly personaId: string;
  readonly file: File;
  readonly onProgress?: (fraction: number) => void;
}): Promise<KycUploadResult> {
  const { persona, personaId, file, onProgress } = args;

  if (!ALLOWED_MIME.includes(file.type)) {
    throw new Error(
      `Upload must be a JPG, PNG, WebP, or PDF (got ${file.type || 'unknown type'}).`,
    );
  }
  if (file.size > MAX_BYTES) {
    throw new Error(`File is too big — keep under ${MAX_BYTES / 1024 / 1024} MB.`);
  }

  // Stable filename with a timestamp prefix so the most-recent upload
  // sorts last in the bucket listing for super-admin review.
  const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_').slice(0, 80);
  const stamp = new Date().toISOString().replace(/[:.]/g, '-');
  const folder = persona === 'agent' ? 'agents' : 'recruiters';
  const storagePath = `${folder}/${personaId}/kyc/${stamp}-${safeName}`;

  await new Promise<void>((resolve, reject) => {
    const task = uploadBytesResumable(ref(storage, storagePath), file, {
      contentType: file.type,
    });
    task.on(
      'state_changed',
      (snap) => {
        if (onProgress && snap.totalBytes > 0) {
          onProgress(snap.bytesTransferred / snap.totalBytes);
        }
      },
      (err) => reject(err),
      () => resolve(),
    );
  });

  await callSubmitKycDocument({
    persona,
    storagePath,
    contentType: file.type,
    sizeBytes: file.size,
  });

  return {
    storagePath,
    sizeBytes: file.size,
    contentType: file.type,
  };
}
